/** Longest-prefix route → browser tab / header page name (Super Admin portal). */
const ROUTE_PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/manage-agent': 'Manage Agent',
  '/create-agent/agent-details': 'Agent Details',
  '/create-agent/training-questions': 'Training Questions',
  '/create-agent/simulate-agent/kyc/verify-otp': 'Simulate Agent — Verify OTP',
  '/create-agent/simulate-agent/kyc/instructions': 'Simulate Agent — KYC Instructions',
  '/create-agent/simulate-agent/kyc/aadhaar': 'Simulate Agent — Aadhaar',
  '/create-agent/simulate-agent/kyc/start': 'Simulate Agent — KYC Start',
  '/create-agent/simulate-agent/interview': 'Simulate Agent — Interview',
  '/create-agent/simulate-agent/results': 'Simulate Agent — Results',
  '/create-agent/simulate-agent/docs': 'Simulate Agent — Documents',
  '/create-agent/simulate-agent': 'Simulate Agent',
  '/create-agent/evaluate': 'Evaluate',
  '/create-agent/deploy-retain': 'Deploy / Retain',
  '/create-agent': 'Create Agent',
  '/manage-client': 'Clients',
  '/manage-client/create-client': 'Add Client',
  '/manage-admin-users': 'Manage Users',
  '/create-admin-user': 'Create User',
  '/system-preferences/manage-permissions': 'Manage Permissions',
  '/system-preferences': 'System Preferences',
  '/manage-roles': 'System Preferences',
  '/audit-trail/session': 'Session Detail',
  '/audit-trail': 'Audit Trail',
  '/audit-logs': 'Audit Trail',
  '/integrations': 'Integrations',
  '/subscriptions': 'Subscriptions',
  '/rule-packs': 'Rule Packs',
  '/llm-models': 'LLM Models',
  '/llm-config': 'LLM Task Configuration',
  '/reports': 'Reports',
  '/system-preferences/email-template': 'Edit Email Template',
  '/user-setting/email-template': 'Edit Email Template',
  '/user-setting': 'System Preferences',
  '/profile': 'Profile',
  '/user-account-settings': 'Account Settings',
  '/mailbox': 'Mailbox',
  '/calendar': 'Calendar',
  '/chat': 'Chat',
  '/auth/signin': 'Sign In',
  '/auth/signup': 'Sign Up',
  '/auth/forgot-password': 'Forgot Password',
  '/auth/reset-password': 'Reset Password',
};

const UUID_OR_NUMERIC = /^[0-9a-f-]{8,}$|^\d+$/i;

function humanizeSegment(segment: string): string {
  return segment
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0].split('#')[0].toLowerCase().replace(/\/+$/, '');
  return path || '/';
}

/** Resolve a human-readable page title from the URL pathname. */
export function resolvePageTitle(pathname: string): string {
  const path = normalizePath(pathname);

  if (ROUTE_PAGE_TITLES[path]) {
    return ROUTE_PAGE_TITLES[path];
  }

  const entries = Object.entries(ROUTE_PAGE_TITLES)
    .filter(([key]) => key !== '/')
    .sort((a, b) => b[0].length - a[0].length);

  for (const [key, title] of entries) {
    if (path === key || path.startsWith(`${key}/`)) {
      return title;
    }
  }

  const parameterizedTitle = resolveParameterizedRouteTitle(path);
  if (parameterizedTitle) {
    return parameterizedTitle;
  }

  const segments = path.split('/').filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i];
    if (!UUID_OR_NUMERIC.test(seg)) {
      return humanizeSegment(seg);
    }
  }

  return 'Dashboard';
}

function resolveParameterizedRouteTitle(path: string): string | null {
  if (/^\/manage-client\/[^/]+\/edit$/.test(path)) return 'Edit Client';
  if (/^\/manage-client\/[^/]+$/.test(path) && path !== '/manage-client' && path !== '/manage-client/create-client') {
    return 'Client Details';
  }
  if (/^\/manage-agent\/[^/]+$/.test(path) && path !== '/manage-agent') return 'Agent Details';
  if (/^\/reports\/[^/]+$/.test(path) && path !== '/reports') return 'Report Details';
  if (/^\/manage-admin-users\/[^/]+\/edit$/.test(path)) return 'Edit User';
  if (/^\/manage-admin-users\/[^/]+$/.test(path)) return 'User Details';
  if (/^\/system-preferences\/email-template\/[^/]+$/.test(path)) return 'Edit Email Template';
  if (/^\/user-setting\/email-template\/[^/]+$/.test(path)) return 'Edit Email Template';
  return null;
}
